import {
  Alert,
  Avatar,
  Button,
  Col,
  Descriptions,
  Dropdown,
  Form,
  Input,
  Row,
  Space,
  Tag,
  theme,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import Icon from "@ant-design/icons";
import { Icons } from "../../assets/icons";
import {
  Incident,
  IncidentComment,
  IncidentSeverityType,
  IncidentStatusType,
  MAX_PAGE_SIZE,
} from "../../repository/data.default";
import {
  getIncident,
  getIncidentComments,
  addIncidentComments,
  updateIncidentComments,
  deleteIncidentComments,
  updateIncident,
  getIncidentReporter,
  getIncidentTypes,
  getPlatformUsers,
} from "../../repository/data.repository";
import {
  incidentStatusColor,
  incidentSeverityColor,
  incidentStatusGuidance,
  incidentAllowedTransitions,
} from "./IncidentsView.default";
import InModal from "../../controls/InModal";
import IncidentFollowUpsPanel from "./IncidentFollowUpsPanel";
import DynamicForm from "../../controls/DynamicForm";
import ManageStatusBanner from "../../controls/ManageStatusBanner";
import {
  createSearchRequest,
  createScopedSearchRequest,
} from "../../hooks/useSearchRequest";
import { useDialogAction } from "../../hooks/useDialogAction";
import { fmtDateTime as fmt } from "../../utils/date";
import { retry } from "../../utils/http";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";

const { TextArea } = Input;

export interface IncidentManageDialogProps {
  readonly open?: boolean;
  readonly record?: Incident;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

/**
 * Ticket-style Manage surface for an incident. The status/severity banner sits up top, the editable
 * details (type, title, description, severity, status, assignee) on the left, and a scalable comment
 * timeline on the right. Comments and details persist independently — posting/editing a comment does
 * not touch the incident's RowVersion, and Save Details does not depend on the thread. The Status
 * picker only offers legal transitions (mirrors the backend state machine).
 */
function IncidentManageDialog(props: IncidentManageDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const user = useSelector(authorizeSelectors.user);
  const permissions = useSelector(authorizeSelectors.permissions);
  const isAdmin = useSelector(authorizeSelectors.isAppAdmin);
  const canEdit = permissions.can("Incidents", "edit");

  const [incident, setIncident] = useState<Incident | undefined>(props.record);
  const [comments, setComments] = useState<IncidentComment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [reporter, setReporter] = useState<string | null>(null);

  const { loading, setLoading, errorMessage, setErrorMessage, run } =
    useDialogAction();
  const [commentBusy, setCommentBusy] = useState(false);

  const status = incident?.status;
  const companyUid = incident?.companyUid;

  useEffect(() => {
    if (props.open && props.record) {
      setIncident(props.record);
      setErrorMessage("");
      setReporter(null);
      setNewComment("");
      setEditingUid(null);
      form.setFieldsValue({
        incidentType: props.record.incidentTypeUid
          ? { display: props.record.incidentTypeName, value: props.record.incidentTypeUid }
          : undefined,
        title: props.record.title,
        description: props.record.description,
        severity: { display: props.record.severity, value: props.record.severity },
        status: { display: props.record.status, value: props.record.status },
        assignedToUser: props.record.assignedToUserUid
          ? { display: props.record.assignedToUserUserName, value: props.record.assignedToUserUid }
          : undefined,
      });
      loadComments(props.record.uid);
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // Methods

  function loadComments(uid: string): void {
    retry(() =>
      getIncidentComments({
        incidentUid: uid,
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        searchQuery: { conditions: [] },
      }),
    )
      .then((data) =>
        setComments(
          (data.data ?? [])
            .slice()
            .sort((a, b) => dayjs(a.createdOn).valueOf() - dayjs(b.createdOn).valueOf()),
        ),
      )
      .catch(() => undefined);
  }

  function reloadIncident(): void {
    if (!incident) return;
    getIncident(incident.uid).then((fresh) => {
      if (fresh) setIncident(fresh);
    });
  }

  function onSaveDetails(): void {
    if (!incident) return;
    const { incidentType, title, description, severity, status: nextStatus, assignedToUser } =
      form.getFieldsValue();
    run(
      () =>
        updateIncident({
          uid: incident.uid,
          rowVersion: incident.rowVersion,
          title,
          description,
          severity: severity?.value,
          status: nextStatus?.value,
          assignedToUserUid: assignedToUser?.value,
          incidentTypeUid: incidentType?.value,
        }).then(() => reloadIncident()),
      setLoading,
    );
  }

  // Comments persist immediately (they don't carry the incident RowVersion).

  function onPostComment(): void {
    if (!incident || !newComment.trim()) return;
    run(
      () =>
        addIncidentComments({ incidentUid: incident.uid, comment: newComment.trim() }).then(() => {
          setNewComment("");
          loadComments(incident.uid);
        }),
      setCommentBusy,
    );
  }

  function onSaveEdit(item: IncidentComment): void {
    if (!incident || !editingText.trim()) return;
    run(
      () =>
        updateIncidentComments({
          uid: item.uid,
          rowVersion: item.rowVersion,
          comment: editingText.trim(),
        }).then(() => {
          setEditingUid(null);
          loadComments(incident.uid);
        }),
      setCommentBusy,
    );
  }

  function onRemoveComment(item: IncidentComment): void {
    if (!incident) return;
    run(
      () => deleteIncidentComments(item.uid).then(() => loadComments(incident.uid)),
      setCommentBusy,
    );
  }

  function onReveal(): void {
    if (!incident) return;
    getIncidentReporter(incident.uid)
      .then((r) =>
        setReporter(r?.reportedByUserUserName ?? r?.reportedByUserUid ?? "Reporter not available"),
      )
      .catch(() => setReporter("Failed to reveal reporter"));
  }

  // Requests

  // Incident types are a platform-level (unscoped) reference catalog.
  const incidentTypesRequest = createSearchRequest(getIncidentTypes, "name");

  // Assignee picker is scoped to the incident's company.
  const usersRequest = createScopedSearchRequest(
    getPlatformUsers,
    "userName",
    "companyUid",
    () => companyUid,
  );

  // Status options = the current status plus its legal next moves (state machine).
  const statusRequest = () => {
    const allowed = status ? incidentAllowedTransitions[status] : Object.values(IncidentStatusType);
    const set = status ? [status, ...allowed] : allowed;
    return Promise.resolve(set.map((s) => ({ display: s, value: s })));
  };

  const severityRequest = () =>
    Promise.resolve(
      Object.values(IncidentSeverityType).map((s) => ({ display: s, value: s })),
    );

  return (
    <InModal
      open={props.open}
      size="large"
      title="Manage Incident"
      onCancel={props.onCancel}
      actions={
        <Space>
          <Button disabled={loading} onClick={props.onCancel}>
            Close
          </Button>
          {canEdit && (
            <Button type="primary" loading={loading} onClick={onSaveDetails}>
              Save Details
            </Button>
          )}
        </Space>
      }
    >
      {/* Status + severity banner with at-a-glance meta and the admin reveal */}
      <ManageStatusBanner
        wrap
        tagColor={incidentStatusColor(status)}
        status={status}
        chips={
          <Tag color={incidentSeverityColor(incident?.severity)} className="m-0">
            {incident?.severity}
          </Tag>
        }
        guidance={incidentStatusGuidance(status)}
        extra={
          <>
            {incident?.isAnonymous &&
              (reporter ? (
                <Tag className="m-0">Reporter: {reporter}</Tag>
              ) : (
                <Tag className="m-0">Anonymous</Tag>
              ))}
            {isAdmin && incident?.isAnonymous && !reporter && (
              <Button size="small" onClick={onReveal}>
                Reveal reporter
              </Button>
            )}
          </>
        }
      />

      <Row gutter={16} className="flex flex-row">
        {/* Details — editable for users with Incidents:edit, read-only otherwise */}
        <Col flex="1 1 0" className="min-w-0">
          <Typography.Text strong className="mb-2 block">
            Details
          </Typography.Text>
          {canEdit ? (
            <DynamicForm
              form={form}
              schema={{
                groups: [
                  {
                    controls: [
                      { type: "combobox", name: "incidentType", label: "Type", request: incidentTypesRequest },
                      { type: "textbox", name: "title", label: "Title" },
                      { type: "combobox", name: "status", label: "Status", request: statusRequest },
                      { type: "combobox", name: "severity", label: "Severity", request: severityRequest },
                      { type: "combobox", name: "assignedToUser", label: "Assignee", request: usersRequest },
                      { type: "areabox", name: "description", label: "Description", rows: 5 },
                    ],
                  },
                ],
              }}
            />
          ) : (
            <Descriptions
              size="small"
              column={1}
              bordered
              items={[
                { key: "type", label: "Type", children: incident?.incidentTypeName ?? "—" },
                { key: "title", label: "Title", children: incident?.title ?? "—" },
                { key: "assignee", label: "Assignee", children: incident?.assignedToUserUserName ?? "—" },
                { key: "description", label: "Description", children: incident?.description || "—" },
              ]}
            />
          )}

          {/* Read-only context that isn't editable post-creation */}
          <Descriptions
            className="mt-3"
            size="small"
            column={1}
            bordered
            items={[
              { key: "company", label: "Company", children: incident?.companyName ?? "—" },
              { key: "department", label: "Department", children: incident?.departmentName ?? "—" },
              { key: "reportedDate", label: "Reported", children: fmt(incident?.reportedDate) },
              {
                key: "reportedBy",
                label: "Reported By",
                children: incident?.isAnonymous
                  ? "Anonymous"
                  : incident?.reportedByUserUserName ?? "—",
              },
            ]}
          />
        </Col>

        {/* Comments — a scalable timeline that posts/edits immediately */}
        <Col flex="1 1 0" className="flex min-w-0 flex-col">
          <Typography.Text strong className="mb-2 block">
            Comments
          </Typography.Text>

          <div
            className="mb-3 flex flex-col gap-2 overflow-y-auto pr-1"
            style={{ maxHeight: 360 }}
          >
            {comments.length === 0 && (
              <Typography.Text type="secondary">No comments yet.</Typography.Text>
            )}
            {comments.map((item) => {
              const own = user?.userId === item.userUid;
              const editing = editingUid === item.uid;
              // Author can edit + remove; an admin can also remove (moderate offensive content) but not edit.
              const canModerate = own || isAdmin;
              const wasEdited =
                !!item.updatedOn &&
                dayjs(item.updatedOn).isAfter(dayjs(item.createdOn));
              return (
                <div
                  key={item.uid}
                  className="flex flex-row gap-2 p-2"
                  style={{
                    backgroundColor: token.colorFillQuaternary,
                    borderRadius: token.borderRadius,
                  }}
                >
                  <Avatar size="small" style={{ backgroundColor: token.colorPrimary }}>
                    {(item.userUserName ?? "?").charAt(0).toUpperCase()}
                  </Avatar>
                  <Col className="flex min-w-0 flex-auto flex-col">
                    <Row className="flex flex-row flex-nowrap items-center gap-2">
                      <Typography.Text strong className="flex-auto truncate">
                        {item.userUserName}
                      </Typography.Text>
                      <Typography.Text type="secondary" className="whitespace-nowrap text-xs">
                        {fmt(item.createdOn)}
                      </Typography.Text>
                      {wasEdited && (
                        <Tooltip title={`Edited ${fmt(item.updatedOn)}`}>
                          <Typography.Text
                            type="secondary"
                            className="whitespace-nowrap text-xs italic"
                          >
                            (edited)
                          </Typography.Text>
                        </Tooltip>
                      )}
                      {canModerate && !editing && (
                        <Dropdown
                          placement="bottomRight"
                          menu={{
                            items: [
                              ...(own
                                ? [
                                    {
                                      key: "edit",
                                      label: "Edit",
                                      onClick: () => {
                                        setEditingUid(item.uid);
                                        setEditingText(item.comment);
                                      },
                                    },
                                  ]
                                : []),
                              {
                                key: "remove",
                                label: own ? "Remove" : "Remove (moderate)",
                                danger: true,
                                onClick: () => onRemoveComment(item),
                              },
                            ],
                          }}
                        >
                          <Button
                            type="text"
                            size="small"
                            icon={<Icon component={Icons.MoreVertIcon} />}
                          />
                        </Dropdown>
                      )}
                    </Row>
                    {editing ? (
                      <Col className="mt-1 flex flex-col gap-2">
                        <TextArea
                          rows={3}
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                        />
                        <Space className="justify-end">
                          <Button size="small" onClick={() => setEditingUid(null)}>
                            Cancel
                          </Button>
                          <Button
                            type="primary"
                            size="small"
                            loading={commentBusy}
                            disabled={!editingText.trim()}
                            onClick={() => onSaveEdit(item)}
                          >
                            Save
                          </Button>
                        </Space>
                      </Col>
                    ) : (
                      <Typography.Paragraph className="mb-0 whitespace-pre-wrap">
                        {item.comment}
                      </Typography.Paragraph>
                    )}
                  </Col>
                </div>
              );
            })}
          </div>

          {/* Composer — posts immediately, independent of Save Details */}
          <Col className="mt-auto flex flex-col gap-2">
            <TextArea
              rows={3}
              placeholder="Add a comment…"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <Button
              type="primary"
              className="self-end"
              loading={commentBusy}
              disabled={!newComment.trim()}
              onClick={onPostComment}
            >
              Post Comment
            </Button>
          </Col>
        </Col>
      </Row>

      {/* PD-01 §11 — Follow-Up Actions panel, inline below the details/comments
          row so admin can track remediation on the same surface. */}
      <div className="mt-6 border-t pt-4">
        <IncidentFollowUpsPanel incidentUid={incident?.uid} canEdit={canEdit} />
      </div>

      {errorMessage && <Alert className="mt-3" message={errorMessage} type="error" />}
    </InModal>
  );
}

export default IncidentManageDialog;
